import { storageAvailable } from 'ClientStorage';

function memoize(probe: () => boolean): () => boolean {
    let result: boolean | undefined;
    return () => (result ??= probe());
}

// `navigator.cookieEnabled` stays true when a per-site rule blocks cookies, and per-site
// blocking is exactly what canSignIn has to catch. So write a cookie and
// read it back.
const cookiesAvailable = memoize(() => {
    try {
        document.cookie = 'cookietest=1';
        const available = /(?:^|;\s*)cookietest=/.test(document.cookie);
        document.cookie = 'cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT';
        return available;
    } catch {
        // `document.cookie` throws a SecurityError in a sandboxed iframe or a data-URI document.
        return false;
    }
});

const localStorageAvailable = memoize(() => storageAvailable('localStorage'));
const sessionStorageAvailable = memoize(() => storageAvailable('sessionStorage'));

const Capabilities = {
    // Touch hardware exists. Gate an input path on this: touch listeners, gestures, pinch-zoom.
    get touchEnabled(): boolean {
        return window.navigator.maxTouchPoints > 0;
    },

    // Touch is the primary input. Gate look and behaviour on this: autofocus, form layout,
    // hover triggers. A touchscreen laptop reports `touchEnabled` but not `touchPrimary`.
    // This is the JS mirror of the `@media (pointer: coarse)` rules in the stylesheets.
    get touchPrimary(): boolean {
        return window.matchMedia('(pointer: coarse)').matches;
    },

    get localStorage(): boolean {
        return localStorageAvailable();
    },

    get sessionStorage(): boolean {
        return sessionStorageAvailable();
    },

    get cookies(): boolean {
        return cookiesAvailable();
    },

    // Web auth is only the httponly `auth_token` cookie from Auth::SetAuthToken, so
    // localStorage cannot replace it. Mobile keeps its bearer token in Capacitor Preferences.
    get canSignIn(): boolean {
        return !!window.CORDOVA || this.cookies;
    },
};

export default Capabilities;
